/**
 * useThrottle Hook
 * 
 * Returns a throttled callback that only executes at most once per delay period.
 * Unlike debounce (which waits for inactivity), throttle ensures regular execution.
 * 
 * @example
 * const throttledClick = useThrottle(() => handleClick(), 300);
 * <button onClick={throttledClick}>Click</button>
 */

import { useRef, useCallback } from 'react';

/**
 * Creates a throttled function that only invokes at most once per wait period.
 * 
 * @param callback - Function to throttle
 * @param delay - Minimum time between invocations (ms)
 * @returns Throttled callback
 */
export function useThrottle<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay: number
): (...args: Parameters<T>) => void {
  const lastCallRef = useRef<number>(0);
  const callbackRef = useRef(callback);
  
  // Keep callback ref updated
  callbackRef.current = callback;

  return useCallback((...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      callbackRef.current(...args);
    }
  }, [delay]);
}

/**
 * Returns a function to check if enough time has passed since last call.
 * Useful for keyboard handlers where you need to check before calling.
 * 
 * @param delay - Minimum time between allowed actions (ms)
 * @returns Function that returns true if action is allowed
 * 
 * @example
 * const canAction = useThrottleCheck(300);
 * if (canAction()) { doSomething(); }
 */
export function useThrottleCheck(delay: number): () => boolean {
  const lastCallRef = useRef<number>(0);

  return useCallback(() => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      return true;
    }
    return false;
  }, [delay]);
}

